import { useEffect, useState } from 'react';
import { Clock, Loader2 } from 'lucide-react';
import { useUpdateAttendance } from '../../../services/httpServices/queries';
import { HrModal } from './HrModal';
import { DatePicker, TimePicker } from '../../../components/ui/DatePicker';
import type { Attendance } from '../../../services/httpServices/hrService';

const ATTENDANCE_STATUSES = [
  { value: 'PRESENT', label: 'Present' },
  { value: 'ABSENT', label: 'Absent' },
  { value: 'LATE', label: 'Late' },
  { value: 'HALF_DAY', label: 'Half Day' },
  { value: 'ON_LEAVE', label: 'On Leave' },
  { value: 'HOLIDAY', label: 'Holiday' },
];

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary';

/** Convert an ISO timestamp into the `HH:mm` value a time input expects. */
function toTimeInput(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

interface AttendanceEditModalProps {
  open: boolean;
  record: Attendance | null;
  onClose: () => void;
}

export function AttendanceEditModal({ open, record, onClose }: AttendanceEditModalProps) {
  const updateAttendance = useUpdateAttendance();

  const [status, setStatus] = useState('PRESENT');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [notes, setNotes] = useState('');

  const date = record?.date ? String(record.date).slice(0, 10) : '';

  useEffect(() => {
    if (!open || !record) return;
    setStatus(record.status || 'PRESENT');
    setCheckIn(toTimeInput(record.checkIn));
    setCheckOut(toTimeInput(record.checkOut));
    setNotes(record.notes || '');
  }, [open, record]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!record) return;

    const payload: Record<string, unknown> = { status };
    payload.checkIn = checkIn ? `${date}T${checkIn}:00` : null;
    payload.checkOut = checkOut ? `${date}T${checkOut}:00` : null;
    payload.notes = notes.trim() || null;

    updateAttendance.mutate({ id: record.id, data: payload }, { onSuccess: () => onClose() });
  };

  return (
    <HrModal
      open={open}
      onClose={onClose}
      title="Edit Attendance"
      subtitle={date ? `Attendance for ${date}` : undefined}
      icon={Clock}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="attendance-edit-form"
            disabled={updateAttendance.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {updateAttendance.isPending && <Loader2 size={16} className="animate-spin" />}
            Save Changes
          </button>
        </>
      }
    >
      <form id="attendance-edit-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
              {ATTENDANCE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Date</label>
            <DatePicker value={date} onChange={() => undefined} disabled className={inputClass} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Check In</label>
            <TimePicker value={checkIn} onChange={setCheckIn} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Check Out</label>
            <TimePicker value={checkOut} onChange={setCheckOut} className={inputClass} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text mb-1">Notes</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional note"
            className={inputClass}
          />
        </div>
      </form>
    </HrModal>
  );
}
